import { Wifi, WifiOff, Loader2 } from "lucide-react";
import { useSocketStatus } from "@/hooks/useConsoleSocket";
import { Tooltip } from "@/components/ui/Tooltip";
import { S } from "@/lib/strings";
import { cn } from "@/lib/utils";

/**
 * "The browser lost the server" — never to be confused with "a device is
 * offline".
 *
 * These are two different failures with two different remedies, and conflating
 * them is how an operator ends up power-cycling a phone because a proxy hiccuped.
 * This badge is about *our* socket. A device's own reachability is a
 * `DeviceHealthPill`, and its actions still queue.
 */
export function ConnectionBadge({ className }: { className?: string }) {
  const status = useSocketStatus();

  if (status === "open") {
    return (
      <Tooltip content="即時連線正常">
        <span
          className={cn(
            "inline-flex items-center gap-1.5 text-[11px] text-[var(--color-fg-faint)]",
            className,
          )}
        >
          <Wifi size={12} className="text-[#10b981]" />
          <span className="sr-only">{S.connOnline}</span>
        </span>
      </Tooltip>
    );
  }

  if (status === "connecting") {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1.5 text-[11px] text-[var(--color-fg-subtle)]",
          className,
        )}
      >
        <Loader2 size={12} className="animate-spin" />
        {S.connConnecting}
      </span>
    );
  }

  return (
    <Tooltip content={S.connOfflineHint}>
      <span
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full bg-[rgba(245,158,11,0.12)] px-2 py-0.5",
          "text-[11px] font-medium text-[#f59e0b]",
          className,
        )}
      >
        <WifiOff size={12} />
        {S.connOffline}
      </span>
    </Tooltip>
  );
}

/**
 * A marker for data rendered while the socket is down: what you are looking at
 * was true at the last successful load, not now.
 */
export function StaleDataMarker({ className }: { className?: string }) {
  const status = useSocketStatus();
  if (status === "open") return null;
  return (
    <span className={cn("text-[11px] text-[var(--color-fg-faint)] italic", className)}>
      · {S.staleData}
    </span>
  );
}

/** True when live-only controls (stop, dispatch, remote) should be disabled. */
export function useLiveActionsDisabled(): { disabled: boolean; reason?: string } {
  const status = useSocketStatus();
  return status === "open" ? { disabled: false } : { disabled: true, reason: S.liveDisabledOffline };
}
